/**
 * Single preview row — section name + a type-appropriate dummy control. The
 * body NEVER reflects `section.content`/real answers; preview is a
 * structural rehearsal, not a content rendering. `text` reuses
 * `useBriefSectionViewer` so its prose chrome matches the live brief editor;
 * every other type renders a disabled, type-appropriate dummy control.
 */
import {
  Checkbox,
  Input,
  InputFile,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@monksflow/monks-ui';
import { EditorContent } from '@tiptap/react';
import { useTranslation } from 'react-i18next';
import { BRIEF_PROSE_CLASSES, useBriefSectionViewer } from '@/entities/brief';
import { DUMMY_SECTION_DOC } from './BriefTemplatePreviewPanel.constants';
import type { BriefTemplatePreviewSectionProps } from './BriefTemplatePreviewPanel.types';

const PROSE_OVERRIDES = `${BRIEF_PROSE_CLASSES} [&_.ProseMirror]:min-h-[40px]`;

// Matches the prototype's section label — a small uppercase caption, distinct
// from `BRIEF_SECTION_TITLE_CLASSES` (a heading-md style used by the live
// brief editor, not this structural preview).
const PREVIEW_SECTION_LABEL_CLASSES =
  'text-xs font-medium uppercase tracking-wide text-muted-foreground';

function TextDummy() {
  const editor = useBriefSectionViewer(DUMMY_SECTION_DOC);
  if (!editor) return null;
  return <EditorContent editor={editor} className={PROSE_OVERRIDES} />;
}

function DateDummy() {
  return <Input type="date" disabled className="max-w-60" />;
}

function SelectDummy(props: { options: string[] }) {
  const { t } = useTranslation('knowledge');
  return (
    <Select disabled>
      <SelectTrigger data-testid="brief-template-preview-select" className="max-w-80">
        <SelectValue placeholder={t('knowledgeHub.briefTemplatePreviewPanel.selectPlaceholder')} />
      </SelectTrigger>
      <SelectContent>
        {props.options.map((option) => (
          <SelectItem
            key={option}
            value={option || t('knowledgeHub.briefTemplatePreviewPanel.selectPlaceholder')}
          >
            {option}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function ChecklistDummy(props: { options: string[] }) {
  return (
    <div className="flex flex-col gap-2">
      {props.options.map((option, index) => (
        <label key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
          <Checkbox disabled />
          {option}
        </label>
      ))}
    </div>
  );
}

function UrlDummy() {
  return <Input type="url" disabled placeholder="https://example.com" className="max-w-96" />;
}

function FileDummy() {
  return <InputFile disabled className="max-w-96" />;
}

function BooleanDummy(props: { trueLabel: string; falseLabel: string }) {
  return (
    <RadioGroup disabled value={props.trueLabel} className="flex flex-row gap-4">
      <label className="flex items-center gap-2 text-sm text-muted-foreground">
        <RadioGroupItem value={props.trueLabel} />
        {props.trueLabel}
      </label>
      <label className="flex items-center gap-2 text-sm text-muted-foreground">
        <RadioGroupItem value={props.falseLabel} />
        {props.falseLabel}
      </label>
    </RadioGroup>
  );
}

function SectionDummy(props: BriefTemplatePreviewSectionProps) {
  const { section } = props;
  const config = section.config;

  switch (section.type) {
    case 'date':
      return <DateDummy />;
    case 'select':
      return (
        <SelectDummy options={Array.isArray(config.options) ? (config.options as string[]) : []} />
      );
    case 'checklist':
      return (
        <ChecklistDummy
          options={Array.isArray(config.options) ? (config.options as string[]) : []}
        />
      );
    case 'url':
      return <UrlDummy />;
    case 'file':
      return <FileDummy />;
    case 'boolean':
      return (
        <BooleanDummy
          // Backend default (`DEFAULT_SECTION_CONFIG.boolean`), not UI copy —
          // only reached if a section somehow has no persisted trueLabel/falseLabel.
          // eslint-disable-next-line i18next/no-literal-string
          trueLabel={typeof config.trueLabel === 'string' ? config.trueLabel : 'Yes'}
          // eslint-disable-next-line i18next/no-literal-string
          falseLabel={typeof config.falseLabel === 'string' ? config.falseLabel : 'No'}
        />
      );
    case 'text':
    default:
      return <TextDummy />;
  }
}

export function BriefTemplatePreviewSection(props: BriefTemplatePreviewSectionProps) {
  return (
    <section data-testid="brief-template-preview-section" className="flex flex-col gap-2">
      <h2 className={PREVIEW_SECTION_LABEL_CLASSES}>{props.section.name}</h2>
      <SectionDummy section={props.section} />
    </section>
  );
}
